import React from 'react';
import { motion } from 'framer-motion';
import { EyeIcon, RefreshCwIcon } from 'lucide-react';
import { InteriorSlide } from './SlideShell';
import type { SlideProps } from './SlideShell';
const GOVERNANCE_DASHBOARD = 'https://cdn.magicpatterns.com/uploads/5Wc9ZZaP7QNjMKZ7z6a6XX/Screenshot_2026-09-11_at_15.57.41.png';
const METRICS = [{
  value: '6',
  label: 'Snaks live to HCPs'
}, {
  value: '14',
  label: 'Snaks in the portfolio'
}, {
  value: '7',
  label: 'Campaigns in flight'
}, {
  value: '3',
  label: 'Waiting on client'
}] as const;
const LEADERSHIP_INSIGHTS = ['Everything in flight across brands and markets, on one board', 'Exactly what is waiting on the brand team, and what is with us', 'How far HCPs get before they stop, market by market', 'Which campaign the calendar should prioritise next'] as const;
export function SlideGovernanceView({}: SlideProps) {
  return <InteriorSlide surface data-id="element-306" data-mp="14v14n39pdknb">
      <motion.header initial={{
      opacity: 0,
      y: 16
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.5
    }} className="mb-[clamp(1rem,1.7vw,1.6rem)] flex-shrink-0" data-id="element-307" data-mp="14wc811z8kqdw">
        <p className="text-[clamp(0.7rem,1vw,0.9rem)] font-bold uppercase tracking-[0.2em] text-[var(--brand)]" data-id="element-308" data-mp="v0adyj5dycrf">
          The Snak format &amp; data insights
        </p>
        <h2 className="mt-3 max-w-6xl text-[clamp(2rem,3.5vw,3.35rem)] font-extrabold leading-[1.02] tracking-[-0.04em] text-[var(--fg)]" data-id="element-309" data-mp="m271eccdoqbr">
          One governance view across{' '}
          <span className="text-[var(--brand)]" data-id="element-310" data-mp="1epazrygyu6ek">brands, markets and cadence.</span>
        </h2>
        <p className="mt-3 max-w-4xl text-[clamp(0.9rem,1.15vw,1.05rem)] leading-relaxed text-[var(--muted)]" data-id="element-311" data-mp="19yktmj0ptzit">
          Directors see the whole portfolio; CX teams retain the market and campaign
          detail needed to act.
        </p>
      </motion.header>

      <div className="grid min-h-0 flex-1 grid-cols-1 gap-5 lg:grid-cols-12" data-id="element-312" data-mp="22hi0f9tq4zjs">
        <motion.section initial={{
        opacity: 0,
        y: 18
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.16,
        duration: 0.6
      }} className="flex min-h-0 flex-col gap-3 lg:col-span-8" aria-label="Brand intelligence dashboard" data-id="element-313" data-mp="1xn4331vi4ich">
          <figure className="min-h-0 flex-1 overflow-hidden rounded-2xl border border-black/10 bg-white shadow-sm" data-id="element-314" data-mp="27ru7deednyjv">
            <img src={GOVERNANCE_DASHBOARD} alt="Mission Control overview showing live Snaks, HCP sessions, median completion, items waiting on the brand team, brands, markets and campaigns in flight" className="h-full w-full object-contain object-top" data-id="element-315" data-mp="2bse4ihyyb260" />
          </figure>
          <div className="flex flex-shrink-0 items-center gap-3 rounded-xl border border-[var(--brand)]/10 bg-white px-4 py-3" data-id="element-316" data-mp="1ow41hg3bqi0p">
            <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-[var(--brand)]" data-id="element-317" data-mp="3j790u87do8a8">
              Computed, never editorial
            </p>
            <p className="text-xs font-semibold leading-snug text-[var(--fg)]" data-id="element-318" data-mp="3n79siujhmfz8">
              Every status, session and completion figure comes straight from
              the live builds.
            </p>
          </div>
        </motion.section>

        <motion.aside initial={{
        opacity: 0,
        y: 18
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.28,
        duration: 0.6
      }} className="flex min-h-0 flex-col gap-3 lg:col-span-4" data-id="element-319" data-mp="1lcdbufawkocw">
          <div className="grid grid-cols-2 gap-3" data-id="element-320" data-mp="1v8ghhv4yr54l">
            {METRICS.map(metric => <article key={metric.label} className="rounded-2xl border border-[var(--brand)]/10 bg-white px-4 py-3 shadow-sm" data-id="element-321" data-mp="1qa597oubh2oh">
                <p className="text-[clamp(1.75rem,3vw,2.75rem)] font-extrabold leading-none text-[var(--fg)]" data-id="element-322" data-mp="1zbd5609d1rfe">
                  {metric.value}
                </p>
                <p className="mt-2 text-[10px] font-bold uppercase tracking-[0.1em] text-[var(--muted)]" data-id="element-323" data-mp="4xlle7xbhr68">
                  {metric.label}
                </p>
              </article>)}
          </div>

          <article className="flex min-h-0 flex-1 flex-col rounded-2xl border border-[var(--brand)]/10 bg-white p-5 shadow-sm" data-id="element-324" data-mp="bdkdje8g5coj">
            <div className="flex items-center gap-2 text-[var(--brand)]" data-id="element-325" data-mp="gdynj1tlywp7">
              <EyeIcon className="h-5 w-5" aria-hidden="true" data-id="element-326" data-mp="lbrw21idw2sn" />
              <h3 className="text-xs font-extrabold uppercase tracking-[0.16em]" data-id="element-327" data-mp="l9xgz3tsn4wh">
                Leadership can see
              </h3>
            </div>
            <ul className="mt-4 flex flex-1 flex-col justify-around gap-3" data-id="element-328" data-mp="3oryt3p6an1sg">
              {LEADERSHIP_INSIGHTS.map(insight => <li key={insight} className="flex gap-3 text-xs font-semibold leading-snug text-[var(--fg)]" data-id="element-329" data-mp="3tqiiy0o0cxq3">
                  <span className="mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-[var(--brand)]" data-id="element-330" data-mp="2f8sdrqcn73h" />
                  {insight}
                </li>)}
            </ul>
            <div className="mt-4 flex items-start gap-3 border-t border-black/5 pt-4" data-id="element-331" data-mp="360o8fpr5vl90">
              <RefreshCwIcon className="mt-0.5 h-4 w-4 flex-shrink-0 text-[var(--brand)]" aria-hidden="true" data-id="element-332" data-mp="2yhf0m0yazum2" />
              <p className="text-xs leading-snug text-[var(--muted)]" data-id="element-333" data-mp="2tk6h9x1f5kj8">
                The view closes the loop: portfolio gap → calendar priority → campaign → new evidence.
              </p>
            </div>
          </article>
        </motion.aside>
      </div>
    </InteriorSlide>;
}